import {
    updateTabField,
    updateTabFieldArray,
    buildField,
    addFieldsToArray,
    removeFromArray,
    updateTabFieldArrayValidators,
    getFieldValueIfDirty
} from '../../common/utils';
import { NODE_FIELDS, PROTECTED_DOMAIN_FIELDS, COMMON_FIELDS } from '../../common/fields';
import * as actions from './silverfortNodesActions';
import { commonActionsReducer } from '../../common/actions';
import { validateFQDNWithSecureADSync, validateNodesRadiusAccountsUnique } from '../../common/validators';
import { SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';

const {
    NODE_ID,
    NODE_NAME,
    DOMAINS,
    DOMAIN,
    LDAP_URL,
    USE_SECURE_LDAP,
    ADSYNC_NODE,
    RADIUS_NODE,
    DC_ADDRESS,
    RADIUS_MACHINE_ACCOUNT,
    HTTP_PROXY,
    PROXY_ACCOUNT,
    NODE_ENABLED
} = NODE_FIELDS;

const { DOMAIN_SERVICE_ACCOUNT } = PROTECTED_DOMAIN_FIELDS;
const { LDAP_IP_OPTIONS } = COMMON_FIELDS;

export const fieldsReducer = (state, action) => {
    const systemMode = action.payload.systemMode;
    let newState = state;
    switch (action.type) {
        case actions.UPDATE_NODE:
            if (action.payload.fieldId === DOMAINS.ID) {
                if (action.payload.arrFieldId === RADIUS_NODE.ID) {
                    updateTabFieldArrayValidators(
                        state,
                        action.payload.nodeId,
                        action.payload.fieldId,
                        action.payload.index,
                        DC_ADDRESS.ID,
                        action.payload.value ? { required: true } : {}
                    );
                    updateTabFieldArrayValidators(
                        state,
                        action.payload.nodeId,
                        action.payload.fieldId,
                        action.payload.index,
                        RADIUS_MACHINE_ACCOUNT.ID,
                        action.payload.value ? { userRequired: true } : {}
                    );
                }

                newState = updateTabFieldArray(
                    state,
                    action.payload.nodeId,
                    action.payload.fieldId,
                    action.payload.index,
                    action.payload.arrFieldId,
                    action.payload.value
                );

                if (action.payload.arrFieldId === RADIUS_MACHINE_ACCOUNT.ID) {
                    validateNodesRadiusAccountsUnique(newState, action.payload.nodeId);
                }
                if (
                    action.payload.arrFieldId === DC_ADDRESS.ID ||
                    action.payload.arrFieldId === LDAP_URL.ID ||
                    action.payload.arrFieldId === USE_SECURE_LDAP.ID
                ) {
                    validateFQDNWithSecureADSync(newState[action.payload.nodeId].fields);
                }
            } else {
                newState = updateTabField(state, action.payload.nodeId, action.payload.fieldId, action.payload.value);
                if (action.payload.fieldId === NODE_NAME.ID) {
                    newState[action.payload.nodeId].name = action.payload.value;
                }
            }
            return newState;
        case actions.ADD_NODE_DOMAIN:
            newState = addFieldsToArray(state, action.payload.nodeId, DOMAINS.ID, getNewNodeDomainFields(systemMode));
            return newState;
        case actions.DELETE_NODE_DOMAIN:
            newState = removeFromArray(state, action.payload.nodeId, action.payload.fieldId, action.payload.index);
            return newState;
        case actions.DISCARD_NODE:
            state[action.payload.nodeId] = initNodeFields(
                action.payload.nodeId,
                action.payload.settings,
                true,
                systemMode
            );
            return state;
        case actions.DELETE_NODE:
            return deleteNode(state, action.payload.nodeId);
        case actions.LDAP_IP_ADDED:
            newState[LDAP_IP_OPTIONS.ID] = buildField(
                LDAP_IP_OPTIONS,
                newState[LDAP_IP_OPTIONS.ID].value.concat(action.payload.value)
            );
            return newState;
        default:
            return commonActionsReducer(state, action, initFieldsState);
    }
};

export const initFieldsState = ({ settings }, wizard) => {
    const { NodesConfigurations } = settings;
    if (!NodesConfigurations) {
        return {};
    }
    const nodesFields = {};
    for (const nodeId in NodesConfigurations) {
        nodesFields[nodeId] = initNodeFields(nodeId, settings, wizard);
    }

    nodesFields[LDAP_IP_OPTIONS.ID] = buildField(LDAP_IP_OPTIONS, settings.LdapServerOptions);

    return nodesFields;
};

const initNodeFields = (nodeId, settings, wizard) => {
    const { NodesConfigurations } = settings;
    const nodeObj = { ...NodesConfigurations[nodeId] };

    const nodeFields = {
        id: nodeId,
        name: nodeObj.Name,
        fields: {
            [NODE_ID.ID]: buildField(NODE_ID, nodeId),
            [NODE_NAME.ID]: buildField(NODE_NAME, nodeObj.Name, { required: true, maxLength: 40 }),
            [DOMAINS.ID]: buildField(
                DOMAINS,
                mapNodeDomainsFields(
                    nodeObj.domainsConfiguration,
                    nodeId,
                    settings.DomainsConfigurations,
                    settings.LdapServerOptions,
                    settings.systemMode
                )
            ),
            [HTTP_PROXY.ID]: buildField(HTTP_PROXY, nodeObj.HTTPProxy, { url: true }),
            [PROXY_ACCOUNT.ID]: buildField(PROXY_ACCOUNT, {
                user: nodeObj.proxyAccount,
                pass: ''
            })
        },
        validators: {
            uniqueName: { fieldId: NODE_NAME.ID, caseSensitive: true },
            uniqueAccounts: true
        }
    };
    if (wizard) {
        nodeFields.fields[NODE_ENABLED.ID] = buildField(NODE_ENABLED, false);
    }

    return nodeFields;
};

const mapNodeDomainsFields = (
    nodeDomainsConfiguration,
    nodeId,
    domainsConfigurations,
    ldapServerOptions,
    systemMode
) => {
    const nodeDomainsFields = [];
    for (const domainId in nodeDomainsConfiguration) {
        const domain = domainsConfigurations[domainId || ''];
        const nodeDomainConf = nodeDomainsConfiguration[domainId];

        let isAdsyncNode = false;
        let domainServiceAccountUser = '';
        if (domain) {
            isAdsyncNode = Array.isArray(domain.ADSyncDomainNodes) && domain.ADSyncDomainNodes.includes(nodeId);
            domainServiceAccountUser = domain.ServiceAccountUsername || '';
        }

        const ldapIpSelectedOption = ldapServerOptions
            ? ldapServerOptions.find((option) => option.value === (nodeDomainConf.NodeLdapAuthUrl || ''))
            : { value: '' };
        const dcAddressSelectedOption = ldapServerOptions
            ? {
                  value: ldapServerOptions
                      .filter((option) => nodeDomainConf.DCIps?.includes(option.value))
                      .map((option) => option.value)
              }
            : { value: [] };

        nodeDomainsFields.push({
            [DOMAIN.ID]: buildField(DOMAIN, domainId, { required: true }),
            [LDAP_URL.ID]: buildField(LDAP_URL, ldapIpSelectedOption ? ldapIpSelectedOption.value : '', {
                required: systemMode === SYSTEM_MODES.ON_PREM,
                domainOrIp: true
            }),

            [USE_SECURE_LDAP.ID]: buildField(USE_SECURE_LDAP, nodeDomainConf.shouldSecureNodeLDAP),
            [ADSYNC_NODE.ID]: buildField(ADSYNC_NODE, isAdsyncNode),
            [RADIUS_NODE.ID]: buildField(RADIUS_NODE, !!nodeDomainConf.radiusAccount),
            [DC_ADDRESS.ID]: buildField(
                DC_ADDRESS,
                dcAddressSelectedOption ? dcAddressSelectedOption.value : [],
                nodeDomainConf.radiusAccount ? { required: true, domainOrIpArray: true } : {}
            ),
            [RADIUS_MACHINE_ACCOUNT.ID]: buildField(
                RADIUS_MACHINE_ACCOUNT,
                {
                    user: nodeDomainConf.radiusAccount,
                    pass: ''
                },
                nodeDomainConf.radiusAccount ? { userRequired: true } : {}
            ),
            [DOMAIN_SERVICE_ACCOUNT.ID]: buildField(DOMAIN_SERVICE_ACCOUNT, {
                user: domainServiceAccountUser,
                pass: ''
            })
        });
    }

    return nodeDomainsFields;
};

const getNewNodeDomainFields = (systemMode) => {
    return {
        [DOMAIN.ID]: buildField(DOMAIN, null, { required: true }),
        [LDAP_URL.ID]: buildField(LDAP_URL, null, {
            required: systemMode === SYSTEM_MODES.ON_PREM
        }),

        [USE_SECURE_LDAP.ID]: buildField(USE_SECURE_LDAP, true),
        [ADSYNC_NODE.ID]: buildField(ADSYNC_NODE, false),
        [RADIUS_NODE.ID]: buildField(RADIUS_NODE, false),
        [DC_ADDRESS.ID]: buildField(DC_ADDRESS, []),
        [RADIUS_MACHINE_ACCOUNT.ID]: buildField(RADIUS_MACHINE_ACCOUNT, {
            user: null,
            pass: ''
        }),
        [DOMAIN_SERVICE_ACCOUNT.ID]: buildField(DOMAIN_SERVICE_ACCOUNT, {
            user: null,
            pass: ''
        })
    };
};

export const mapToSettings = (saveAll, selectedTab, fields, currentSettings) => {
    const newSettings = {
        NodesConfigurations: { ...currentSettings.NodesConfigurations },
        DomainsConfigurations: { ...currentSettings.DomainsConfigurations }
    };
    if (saveAll) {
        for (const nodeId in currentSettings.NodesConfigurations) {
            const newNodeObj = mapNodeToSettings(fields[nodeId].fields, currentSettings.DomainsConfigurations);
            newSettings.NodesConfigurations[nodeId] = newNodeObj;

            const newDomainsConfigurations = mapDomainToSettings(
                nodeId,
                newSettings.DomainsConfigurations,
                fields[nodeId].fields,
                currentSettings.NodesConfigurations[nodeId].domainsConfiguration
            );
            newSettings.DomainsConfigurations = newDomainsConfigurations;
        }
    } else {
        const newNodeObj = mapNodeToSettings(fields[selectedTab].fields, currentSettings.DomainsConfigurations);
        newSettings.NodesConfigurations[selectedTab] = newNodeObj;
        const newDomainsConfigurations = mapDomainToSettings(
            selectedTab,
            newSettings.DomainsConfigurations,
            fields[selectedTab].fields,
            currentSettings.NodesConfigurations[selectedTab].domainsConfiguration
        );
        newSettings.DomainsConfigurations = newDomainsConfigurations;
    }

    newSettings.LdapServerOptions = fields[LDAP_IP_OPTIONS.ID].value;
    return newSettings;
};

export const mapNodeToSettings = (nodeFields, domainsConfigurations) => {
    const newNodeObj = {};

    newNodeObj.Name = getFieldValueIfDirty(nodeFields[NODE_NAME.ID]);
    newNodeObj.domainsConfiguration = getFieldValueIfDirty(
        nodeFields[DOMAINS.ID],
        mapSettingsNodeDomainConfig(nodeFields[DOMAINS.ID].value, domainsConfigurations)
    );
    newNodeObj.HTTPProxy = getFieldValueIfDirty(nodeFields[HTTP_PROXY.ID]);
    newNodeObj.proxyAccount = getFieldValueIfDirty(
        nodeFields[PROXY_ACCOUNT.ID],
        nodeFields[PROXY_ACCOUNT.ID].value.user
    );
    newNodeObj.proxyAccountPassword = getFieldValueIfDirty(
        nodeFields[PROXY_ACCOUNT.ID],
        nodeFields[PROXY_ACCOUNT.ID].value.pass
    );

    return newNodeObj;
};

export const mapNodeToSettingsByWizard = (nodeFields, domainsConfigurations) => {
    const newNodeObj = {};
    newNodeObj.Name = nodeFields[NODE_NAME.ID].value;
    newNodeObj.domainsConfiguration = mapSettingsNodeDomainConfig(nodeFields[DOMAINS.ID].value, domainsConfigurations);
    newNodeObj.HTTPProxy = nodeFields[HTTP_PROXY.ID].value;
    newNodeObj.proxyAccount = nodeFields[PROXY_ACCOUNT.ID].value.user;
    newNodeObj.proxyAccountPassword = nodeFields[PROXY_ACCOUNT.ID].value.pass;

    return newNodeObj;
};

const mapSettingsNodeDomainConfig = (nodeDomainsFields, nodeDomainsConfigurations) => {
    const domainsConfiguration = {};
    for (const nodeDomainFields of nodeDomainsFields) {
        const nodeDomainId = nodeDomainFields[DOMAIN.ID].value;
        const nodeDomain = nodeDomainId ? nodeDomainsConfigurations[nodeDomainId] : null;
        const nodeLdapAuthUrl = nodeDomainFields[LDAP_URL.ID].value ? nodeDomainFields[LDAP_URL.ID].value : '';
        let radiusAccount = null;
        let radiusAccountPassword = null;
        if (nodeDomainFields[RADIUS_NODE.ID].value) {
            radiusAccount = nodeDomainFields[RADIUS_MACHINE_ACCOUNT.ID].value.user;
            radiusAccountPassword = nodeDomainFields[RADIUS_MACHINE_ACCOUNT.ID].value.pass;
        }

        domainsConfiguration[nodeDomainId] = {
            NodeLdapAuthUrl: nodeLdapAuthUrl,
            shouldSecureNodeLDAP: nodeDomainFields[USE_SECURE_LDAP.ID].value,
            securedAdsync:
                !!(nodeDomain && nodeDomain.rootCACertificateData) && nodeDomainFields[USE_SECURE_LDAP.ID].value,
            DCIps: nodeDomainFields[DC_ADDRESS.ID].value ? nodeDomainFields[DC_ADDRESS.ID].value : nodeLdapAuthUrl,
            radiusAccount: radiusAccount,
            radiusAccountPassword: radiusAccountPassword
        };
    }

    return domainsConfiguration;
};

export const mapDomainToSettings = (nodeId, currentDomainsConfigurations, nodeFields, oldNodeDomains) => {
    const newDomainsConfigurations = { ...currentDomainsConfigurations };

    for (const oldNodeDomainId in oldNodeDomains) {
        const nodeDomain = nodeFields[DOMAINS.ID].value.find(
            (nodeDomain) => nodeDomain[DOMAIN.ID].value === oldNodeDomainId
        );
        if (oldNodeDomainId in newDomainsConfigurations && (!nodeDomain || !nodeDomain[ADSYNC_NODE.ID].value)) {
            const newDomain = { ...newDomainsConfigurations[oldNodeDomainId] };
            newDomain.ADSyncDomainNodes = (newDomain.ADSyncDomainNodes || []).filter(
                (domainNodeId) => domainNodeId !== nodeId
            );
            newDomainsConfigurations[oldNodeDomainId] = newDomain;
        }
    }
    for (const nodeDomainFields of nodeFields[DOMAINS.ID].value) {
        const domainId = nodeDomainFields[DOMAIN.ID].value;
        if (!domainId || !(domainId in newDomainsConfigurations)) {
            continue;
        }
        if (nodeDomainFields[ADSYNC_NODE.ID].value) {
            const newDomain = { ...newDomainsConfigurations[domainId] };
            const domainAdsyncNodes = [...(newDomain.ADSyncDomainNodes || [])];
            if (!domainAdsyncNodes.includes(nodeId)) {
                domainAdsyncNodes.push(nodeId);
            }
            newDomain.ADSyncDomainNodes = domainAdsyncNodes;
            newDomainsConfigurations[domainId] = newDomain;
        }
        if (
            nodeDomainFields[DOMAIN_SERVICE_ACCOUNT.ID] &&
            nodeDomainFields[DOMAIN_SERVICE_ACCOUNT.ID].value &&
            nodeDomainFields[DOMAIN_SERVICE_ACCOUNT.ID].value.pass
        ) {
            const newDomain = { ...newDomainsConfigurations[domainId] };
            newDomain.ServiceAccountUsername = nodeDomainFields[DOMAIN_SERVICE_ACCOUNT.ID].value.user;
            newDomain.ServiceAccountPassword = nodeDomainFields[DOMAIN_SERVICE_ACCOUNT.ID].value.pass;
            newDomainsConfigurations[domainId] = newDomain;
        }
    }

    return newDomainsConfigurations;
};

const deleteNode = (fields, nodeId) => {
    const newFields = { ...fields };
    delete newFields[nodeId];
    return newFields;
};
